import Link from 'next/link';
import Avatar from '@savvy-kit/components/avatar';
import { Badge } from '@savvy-kit/components/badge';
import { Button, buttonVariants } from '@savvy-kit/components/button';
import { Sheet, SheetBody, SheetContent, SheetHeader } from '@savvy-kit/components/sheet';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import Tag from '@savvy-kit/patterns/tag';
import { startCase } from 'es-toolkit';
import { ExternalLink } from 'lucide-react';
import { parseLocalDate } from 'src/utils/js-date';

import type { ConnectionEntry, ContactWebGraphNode } from './types';
import { SPECIALTY_LABELS } from '../constants';
import { HouseholdAvatar } from './household-avatar';

const AUM_FORMATTER = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const WORKING_TOGETHER_SINCE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

function formatWorkingTogetherSince(value: string | null): string {
  const date = parseLocalDate(value);
  return date === undefined ? 'Not set' : WORKING_TOGETHER_SINCE_FORMATTER.format(date);
}

type Props = {
  readonly node: ContactWebGraphNode | null;
  readonly connections: readonly ConnectionEntry[];
  readonly workingTogetherSince: string | null;
  readonly onSelectNode: (nodeId: string) => void;
  readonly onClose: () => void;
};

export function DetailPanel({ node, connections, workingTogetherSince, onSelectNode, onClose }: Props) {
  return (
    <Sheet open={node !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent size="sm">
        {node && (
          <>
            <SheetHeader>
              <DetailHeader node={node} />
            </SheetHeader>
            <SheetBody className="flex flex-col overflow-y-auto gap-3">
              {node.kind === 'household' ? (
                <HouseholdDetails
                  node={node}
                  connections={connections}
                  onSelectNode={onSelectNode}
                  workingTogetherSince={workingTogetherSince}
                />
              ) : (
                <ProfessionalDetails node={node} connections={connections} onSelectNode={onSelectNode} />
              )}

              {(node.kind !== 'household' || node.salesforceAccountId !== null) && (
                <Link
                  href={
                    node.kind === 'household'
                      ? `/advisor/dashboard/client/${node.salesforceAccountId}`
                      : `/advisor/dashboard/professional-contacts/${node.repId}`
                  }
                  className={cn(buttonVariants({ variant: 'secondary' }), 'mt-1 self-start')}
                >
                  View full profile
                  <ExternalLink className="size-4 shrink-0 text-secondary" />
                </Link>
              )}
            </SheetBody>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function DetailHeader({ node }: { readonly node: ContactWebGraphNode }) {
  return (
    <div className="flex items-center gap-2.5">
      {node.kind === 'household' ? (
        <HouseholdAvatar size="lg" />
      ) : (
        <Avatar size="lg" imageUrl={node.photoUrl} firstName={node.name} lastName="" />
      )}
      <div className="flex flex-col">
        <Text variant="subtitle-lg" color="dark">
          {node.name}
        </Text>
        <Text variant="body-sm" color="secondary">
          {node.kind === 'household' ? 'Household' : 'Professional contact'}
        </Text>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="space-y-1">
      <Text variant="subtitle-sm" color="dark">
        {label}
      </Text>
      <Text variant="body-sm" color="dark" className="truncate">
        {value}
      </Text>
    </div>
  );
}

function HouseholdDetails({
  node,
  connections,
  onSelectNode,
  workingTogetherSince,
}: {
  readonly node: Extract<ContactWebGraphNode, { kind: 'household' }>;
  readonly connections: readonly ConnectionEntry[];
  readonly onSelectNode: (nodeId: string) => void;
  readonly workingTogetherSince: string | null;
}) {
  const professionalsContacts = connections.filter((connection) => connection.label === 'Served by');
  const referredByContacts = connections.filter((connection) => connection.label === 'Referred by');
  const refersToContacts = connections.filter((connection) => connection.label === 'Refers to');

  return (
    <div className="flex flex-col gap-2">
      {node.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-1">
          {node.tags.map((tag) => {
            const orgTag = tag?.organizationHouseholdTag;
            if (!orgTag) return null;
            return (
              <Badge key={tag.id} variant="secondary-muted">
                {orgTag.organizationHouseholdCategory?.emoji} {orgTag.name}
              </Badge>
            );
          })}
        </div>
      )}

      <DetailRow label="Working together since" value={formatWorkingTogetherSince(workingTogetherSince)} />
      <DetailRow label="AUM" value={node.savvyAum === null ? '—' : AUM_FORMATTER.format(Number(node.savvyAum))} />
      <DetailRow label="Stage" value={node.householdStage === null ? '—' : startCase(node.householdStage)} />
      <DetailRow label="Primary advisor" value={node.advisorName ?? '—'} />
      <ConnectionGroup label="Professional contacts" entries={professionalsContacts} onSelectNode={onSelectNode} />
      <ConnectionGroup label="Referred by" entries={referredByContacts} onSelectNode={onSelectNode} />
      <ConnectionGroup label="Refers to" entries={refersToContacts} onSelectNode={onSelectNode} />
    </div>
  );
}

function ProfessionalDetails({
  node,
  connections,
  onSelectNode,
}: {
  readonly node: Extract<ContactWebGraphNode, { kind: 'professional' }>;
  readonly connections: readonly ConnectionEntry[];
  readonly onSelectNode: (nodeId: string) => void;
}) {
  const householdsServed = connections.filter((connection) => connection.label === 'Serves');
  const referredByContacts = connections.filter((connection) => connection.label === 'Referred by');
  const refersToContacts = connections.filter((connection) => connection.label === 'Refers to');
  return (
    <div className="flex flex-col gap-2">
      {node.specialties.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {node.specialties.map((specialty) => (
            <Tag key={specialty} type="read-only">
              {SPECIALTY_LABELS[specialty]}
            </Tag>
          ))}
        </div>
      )}
      {node.description && (
        <Text variant="body-sm" color="secondary">
          {node.description}
        </Text>
      )}
      <ConnectionGroup label="Households served" entries={householdsServed} onSelectNode={onSelectNode} />
      <ConnectionGroup label="Referred by" entries={referredByContacts} onSelectNode={onSelectNode} />
      <ConnectionGroup label="Refers to" entries={refersToContacts} onSelectNode={onSelectNode} />
    </div>
  );
}

function ConnectionGroup({
  label,
  entries,
  onSelectNode,
}: {
  readonly label: string;
  readonly entries: readonly ConnectionEntry[];
  readonly onSelectNode: (nodeId: string) => void;
}) {
  if (entries.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1 space-y-1">
      <Text variant="subtitle-sm" color="dark">
        {label} ({entries.length})
      </Text>
      {entries.map((connection) => (
        <div key={connection.nodeId}>
          <Button
            variant="ghost"
            className="flex w-full items-center justify-between text-left !px-0 py-0.5 h-auto rounded-none"
            onClick={() => onSelectNode(connection.nodeId)}
          >
            <Text color="secondary" className="text-sm truncate font-light">
              {connection.name}
            </Text>
          </Button>
        </div>
      ))}
    </div>
  );
}
